import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Container, Grid } from '@mui/material';
import { Helmet } from 'react-helmet-async';

// Components
import BackdropLoader from '../../components/loading/BackdropLoader';
import BlogCard from './BlogCard';
import NoBlogsFound from './NoBlogsFound';

// Utils
import { blogCategoryFilter, sortByKey } from '../../hooks/utilsFn/utilsFn';

const Blogpage = () => {
    const [blogsData, setBlogsData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [filterLoading, setFilterLoading] = useState(false);

    const [filteredBlogs, setFilteredBlogs] = useState({
        isFilter: false,
        data: [],
        isDataFound: true,
    });

    const [searchParams] = useSearchParams();
    const location = useLocation();

    const canonicalUrl = useMemo(
        () => `https://www.99gift.ae${location.pathname}`,
        [location.pathname]
    );

    // 🔹 Fetch static JSON
    useEffect(() => {
        const fetchStaticBlogs = async () => {
            try {
                const res = await fetch('/blogs.json');
                const json = await res.json();
                setBlogsData(json?.data || []);
            } catch (err) {
                console.error('Failed to load blogs.json', err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchStaticBlogs();
    }, []);

    // 🔹 Filter by category
    const dataFilter = async (data, category) => {
        const filtered = await blogCategoryFilter(data, 'categroies', category);

        if (!filtered?.length) {
            setFilteredBlogs({
                isFilter: true,
                data: [],
                isDataFound: false,
            });
        } else {
            const sortedData = sortByKey(filtered, 'updated_at', 'desc');
            setFilteredBlogs({
                isFilter: true,
                data: sortedData,
                isDataFound: true,
            });
        }

        setFilterLoading(false);
    };

    // 🔹 Handle query params
    useEffect(() => {
        if (!searchParams?.size) {
            setFilteredBlogs({
                isFilter: false,
                data: [],
                isDataFound: true,
            });
            setFilterLoading(false);
        } else {
            setFilterLoading(true);
            const category = searchParams.get('category');
            dataFilter(blogsData, category);
        }
    }, [searchParams, blogsData]);

    return (
        <>
            <BlogMeta canonicalUrl={canonicalUrl} />

            <Container
                maxWidth="xl"
                sx={{
                    backgroundColor: 'white.main',
                    mt: { xs: 12, sm: 4 ,md:4,lg: 8},
                    mb: {  xs:4 ,md: 4 },
                    minHeight: { lg: 400 },
                }}
            >
                {isLoading || filterLoading ? (
                    <BackdropLoader open />
                ) : (
                    <Grid container columnSpacing={4}>
                        <Grid item xs={12} md={12}>
                            <Grid container rowSpacing={3} columnSpacing={3} sx={{ justifyContent: 'center' }}>
                                {filteredBlogs.isFilter ? (
                                    filteredBlogs.isDataFound ? (
                                        filteredBlogs.data.map((item) => <BlogCard data={item} key={item.id} />)
                                    ) : (
                                        <NoBlogsFound />
                                    )
                                ) : (
                                    blogsData.map((item) => <BlogCard data={item} key={item.id} />)
                                )}
                            </Grid>
                        </Grid>
                    </Grid>
                )}
            </Container>
        </>
    );
};

export default Blogpage;

/* ================= SEO ================= */

const BlogMeta = ({ canonicalUrl }) => (
    <Helmet>
        <title>Blogs Regarding Gift Cards - 99gift</title>
        <meta name="description" content="Blogs Regarding Gift Cards - 99gift" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content="Blogs Regarding Gift Cards - 99gift" />
        <meta property="og:description" content="Blogs Regarding Gift Cards - 99gift" />
        <link rel="canonical" href={canonicalUrl} />
    </Helmet>
);
